import { useMutation, UseMutationOptions, useQueryClient } from 'react-query';
import { APIError } from '~/src/utils/error';
import { useCurrentOrgFprint } from '../../user';
import { fieldMappingService } from '../services';
import type { FieldMappingResponse, FieldMappingData } from '../types';
import { Project } from '../../project';
import {
  createOptimisticFieldMappingUpdate,
  rollbackOptimisticUpdate,
} from './useOptimisticFieldMappingUpdate';

type UpdateFieldMappingVariables = {
  projectId: number;
  mappingId: number;
  data: Partial<FieldMappingData>;
};

type UpdateFieldMappingContext = {
  previousProject?: Project;
};

export const useUpdateFieldMapping = (
  options: UseMutationOptions<
    FieldMappingResponse,
    APIError,
    UpdateFieldMappingVariables,
    UpdateFieldMappingContext
  > = {},
) => {
  const orgFprint = useCurrentOrgFprint();
  const queryClient = useQueryClient();

  return useMutation<
    FieldMappingResponse,
    APIError,
    UpdateFieldMappingVariables,
    UpdateFieldMappingContext
  >({
    mutationFn: ({ projectId, mappingId, data }) =>
      fieldMappingService.updateFieldMapping(
        orgFprint,
        projectId,
        mappingId,
        data,
      ),
    onMutate: async ({ projectId, mappingId, data }) => {
      return createOptimisticFieldMappingUpdate(
        queryClient,
        projectId,
        (field) => {
          if (field.field_mapping_metadata?.field_mapping_id === mappingId) {
            return {
              ...field,
              field_mapping_metadata: {
                ...field.field_mapping_metadata,
                ...(data.source_name && { source_field: data.source_name }),
                ...(data.status && { status: data.status }),
              },
            };
          }
          return field;
        },
      );
    },
    onError: (err, variables, context) => {
      rollbackOptimisticUpdate(
        queryClient,
        variables.projectId,
        context?.previousProject,
      );
      options.onError?.(err, variables, context);
    },
    onSuccess: (data, variables, context) => {
      options.onSuccess?.(data, variables, context);
    },
  });
};
